import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./TenantBilling.css";
import SettingsTabs from "../Settings/SettingsTabs";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/tenant-billing/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const emptyBilling = {
  id: null,
  billing_entity: "",
  po_required: false,
  po_format: "",
  po_contact: "",
  payment_terms: ""
};

const TenantBilling = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ ...emptyBilling });
  const [savedData, setSavedData] = useState({ ...emptyBilling });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const [legalEntities, setLegalEntities] = useState([]);
  const [loadingLegalEntities, setLoadingLegalEntities] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Fetch legal entities for the dropdown ----
  useEffect(() => {
    const fetchLegalEntities = async () => {
      const tenantUser = getTenantUser();
      if (!tenantUser?.tenant_id) return;

      try {
        setLoadingLegalEntities(true);

        const response = await axios.post(
          TENANT_RECORDS_API_URL,
          {
            tenant_id: tenantUser.tenant_id,
            tables: ["tenant_legal_entities"]
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data;

        let rows = [];
        if (result?.data?.tenant_legal_entities) {
          rows = result.data.tenant_legal_entities;
        } else if (result?.tenant_legal_entities) {
          rows = result.tenant_legal_entities;
        } else if (Array.isArray(result?.data)) {
          rows = result.data;
        } else if (Array.isArray(result)) {
          rows = result;
        }

        setLegalEntities(rows);
      } catch (error) {
        console.error("Error fetching tenant legal entities:", error);
        setLegalEntities([]);
      } finally {
        setLoadingLegalEntities(false);
      }
    };

    fetchLegalEntities();
  }, []);

  // ---- Fetch existing billing settings ----
  useEffect(() => {
    const fetchExisting = async () => {
      const tenantUser = getTenantUser();

      if (!tenantUser?.tenant_id) {
        setLoadingExisting(false);
        return;
      }

      try {
        setLoadingExisting(true);

        const response = await axios.post(
          TENANT_RECORDS_API_URL,
          {
            tenant_id: tenantUser.tenant_id,
            tables: ["tenant_billings"]
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data;

        let rows = [];
        if (result?.data?.tenant_billings) {
          rows = result.data.tenant_billings;
        } else if (result?.tenant_billings) {
          rows = result.tenant_billings;
        } else if (Array.isArray(result?.data)) {
          rows = result.data;
        } else if (Array.isArray(result)) {
          rows = result;
        }

        const matched = rows.length ? rows[0] : null;

        if (matched) {
          const loaded = {
            id: matched.id,
            billing_entity: matched.billing_entity
              ? String(matched.billing_entity)
              : "",
            po_required: matched.po_required ?? false,
            po_format: matched.po_format || "",
            po_contact: matched.po_contact || "",
            payment_terms: matched.payment_terms || ""
          };

          setFormData(loaded);
          setSavedData(loaded);
        } else {
          setFormData({ ...emptyBilling });
          setSavedData({ ...emptyBilling });
        }
      } catch (error) {
        console.error("Error fetching tenant billing:", error);
        Swal.fire({
          icon: "error",
          title: "Unable to Load Billing",
          text: "Could not fetch tenant billing settings."
        });
        setFormData({ ...emptyBilling });
        setSavedData({ ...emptyBilling });
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const buildPayload = (tenantUser) => ({
    billing_entity: formData.billing_entity
      ? Number(formData.billing_entity)
      : null,
    po_required: !!formData.po_required,
    po_format: formData.po_required
      ? (formData.po_format || "").trim()
      : "",
    po_contact: formData.po_required
      ? (formData.po_contact || "").trim()
      : "",
    payment_terms: (formData.payment_terms || "").trim(),
    tenant: tenantUser.tenant_id
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setFormData({ ...savedData });
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditing) return;

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID is missing from tenant_user."
      });
      return;
    }

    if (formData.po_required && !formData.po_format.trim()) {
      Swal.fire({
        icon: "warning",
        title: "PO Format Required",
        text: "Please enter the Purchase Order format."
      });
      return;
    }

    try {
      setLoading(true);

      const payload = buildPayload(tenantUser);

      if (formData.id) {
        await axios.put(`${API_URL}${formData.id}/`, payload, {
          headers: { "Content-Type": "application/json" }
        });
      } else {
        const response = await axios.post(API_URL, payload, {
          headers: { "Content-Type": "application/json" }
        });

        const newId =
          response.data?.id ||
          response.data?.data?.id ||
          response.data?.pk;

        if (newId) {
          setFormData((prev) => ({ ...prev, id: newId }));
          setSavedData((prev) => ({ ...prev, id: newId }));
        }
      }

      const refreshed = { ...formData };
      setSavedData(refreshed);
      setIsEditing(false);

      await Swal.fire({
        icon: "success",
        title: "Saved Successfully",
        confirmButtonText: "OK"
      });
    } catch (error) {
      console.error("Save Tenant Billing Error:", error);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: `Unable to save tenant billing. ${extractError(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Legal entity label resolver ----
  const getLegalEntityLabel = (entity) => {
    if (!entity) return "";
    return (
      entity.legal_name ||
      entity.name ||
      entity.entity_name ||
      entity.registered_name ||
      entity.company_name ||
      `Legal Entity #${entity.id}`
    );
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-billing-content">
          <SettingsTabs />

          <div className="tenant-billing-page-header">
            <div>
              <h1>Billing Settings</h1>
            </div>
          </div>

          {loadingExisting ? (
            <div className="tenant-billing-loading">
              Loading billing settings...
            </div>
          ) : (
            <form className="tenant-billing-card" onSubmit={handleSubmit}>
              <div className="tenant-billing-card-body">
                {/* Billing Information */}
                <div className="tenant-billing-section">
                  {/* <div className="tenant-billing-section-title">
                    <h3>Billing Information</h3>
                  </div> */}

                  <div className="tenant-billing-grid">
                    <div className="tenant-billing-field">
                      <label>Billing Entity</label>
                      <select
                        name="billing_entity"
                        value={formData.billing_entity}
                        onChange={handleChange}
                        disabled={!isEditing || loadingLegalEntities}
                      >
                        <option value="">
                          {loadingLegalEntities
                            ? "Loading entities..."
                            : "Select billing entity"}
                        </option>

                        {legalEntities.map((entity) => (
                          <option key={entity.id} value={entity.id}>
                            {getLegalEntityLabel(entity)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="tenant-billing-field">
                      <label>Payment Terms</label>
                      <input
                        type="text"
                        name="payment_terms"
                        value={formData.payment_terms}
                        onChange={handleChange}
                        disabled={!isEditing}
                        placeholder="e.g. Net 30"
                      />
                    </div>
                  </div>
                </div>

                {/* Purchase Order Requirements */}
                <div className="tenant-billing-section">
                  {/* <div className="tenant-billing-section-title">
                    <h3>Purchase Order Requirements</h3>
                  </div> */}

                  <div className="tenant-billing-po-toggle-row">
                    <div className="tenant-billing-po-toggle-content">
                      <strong>Purchase Order Required</strong>
                    </div>

                    <label className="tenant-billing-switch">
                      <input
                        type="checkbox"
                        name="po_required"
                        checked={formData.po_required}
                        onChange={handleChange}
                        disabled={!isEditing}
                      />
                      <span className="tenant-billing-slider"></span>
                    </label>
                  </div>

                  {formData.po_required && (
                    <div className="tenant-billing-grid tenant-billing-po-fields">
                      <div className="tenant-billing-field">
                        <label>
                          PO Format <span>*</span>
                        </label>
                        <input
                          type="text"
                          name="po_format"
                          value={formData.po_format}
                          onChange={handleChange}
                          disabled={!isEditing}
                          placeholder="e.g. PO-YYYY-XXXXX"
                        />
                      </div>

                      <div className="tenant-billing-field">
                        <label>PO Contact</label>
                        <input
                          type="text"
                          name="po_contact"
                          value={formData.po_contact}
                          onChange={handleChange}
                          disabled={!isEditing}
                          placeholder="Billing or procurement contact"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="tenant-billing-actions">
                {!isEditing ? (
                  <button
                    type="button"
                    className="tenant-billing-save-btn"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="tenant-billing-cancel-btn"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="tenant-billing-save-btn"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Billing"}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantBilling;